import React, { useEffect, useState, useRef } from "react";
import { getSmtpAccounts } from "../api/api";
import { getConversations, getMessages, deleteContact } from "../api/conversationsApi";
import axios from "axios";
import { createLeadFromConversation } from "../api/leadsApi";

const API_URL = process.env.REACT_APP_API_BASE_URL;

export default function ConversationsPage() {
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [selectedSmtp, setSelectedSmtp] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [sending, setSending] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [unreadConversations, setUnreadConversations] = useState(new Set());
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [mobileView, setMobileView] = useState("list"); // "list" | "chat"
  

  useEffect(() => {
    if (!selectedSmtp) return;
    const interval = setInterval(async () => {
      const res = await getConversations(selectedSmtp);
      const newConvs = res.data;
      setConversations(newConvs);
      const unread = new Set(newConvs.filter(c => !c.is_read).map(c => c.id));
      setUnreadConversations(unread);
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedSmtp]);

  useEffect(() => { loadSmtp(); }, []);

  const loadSmtp = async () => {
    const res = await getSmtpAccounts();
    setSmtpAccounts(res.data);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/imap/request-sync-all`,
        {},
        { withCredentials: true }
      );
      if (res.data.success) {
        setLastSync(new Date().toLocaleTimeString('ru-RU'));
        if (selectedSmtp) {
          setTimeout(async () => {
            try {
              const convRes = await getConversations(selectedSmtp);
              setConversations(convRes.data);
              const unread = new Set(convRes.data.filter(c => !c.is_read).map(c => c.id));
              setUnreadConversations(unread);
            } catch (err) {
              console.error("Ошибка обновления после sync:", err);
            }
          }, 60000);
        }
      }
    } catch (err) {
      alert("Ошибка: " + (err.response?.data?.error || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const selectSmtp = async (smtpId) => {
    const id = Number(smtpId);
    setSelectedSmtp(id);
    const res = await getConversations(id);
    const convs = res.data;
    setConversations(convs);
    const unread = new Set(convs.filter(c => !c.is_read).map(c => c.id));
    setUnreadConversations(unread);
    setActiveConversation(null);
    setMessages([]);
    setShowReplyForm(false);
    setAttachments([]);
    setMobileView("list");
  };

  const openConversation = async (conv) => {
    setActiveConversation(conv);
    setReplySubject(conv.subject ? `Re: ${conv.subject}` : "Re: Ваше письмо");
    setReplyText("");
    setShowReplyForm(false);
    setAttachments([]);
    const res = await getMessages(conv.id);
    setMessages(res.data);
    setMobileView("chat");

    try {
      await axios.post(
        `${API_URL}/conversations/${conv.id}/read`,
        {},
        { withCredentials: true }
      );
      setUnreadConversations(prev => {
        const newSet = new Set(prev);
        newSet.delete(conv.id);
        return newSet;
      });
    } catch (err) {
      console.error("Ошибка отметки прочитанного:", err);
    }
  };

  const handleMoveToLeads = async () => {
  if (!activeConversation) return;
  if (!window.confirm(`Перенести переписку с ${activeConversation.contact_email} в лиды?`)) return;

  try {
    setSending(true);
    await createLeadFromConversation(activeConversation.id, { status: "new" });

    // НЕ удаляем из списка — переписка остаётся и в общих, и в лидах
    // Просто помечаем визуально
    setConversations(prev =>
      prev.map(c =>
        c.id === activeConversation.id ? { ...c, is_lead: true } : c
      )
    );

    alert("✅ Переписка перенесена в лиды. Она также осталась в общих переписках.");
  } catch (err) {
    if (err.response?.status === 409) {
      alert("Эта переписка уже добавлена в лиды.");
    } else {
      alert("Ошибка: " + (err.response?.data?.error || err.message));
    }
  } finally {
    setSending(false);
  }
};

  const handleDeleteFromDatabase = async () => {
    if (!activeConversation || !selectedSmtp) return;

    const confirmDelete = window.confirm(
      `Вы уверены, что хотите удалить контакт "${activeConversation.contact_email}" из всех баз данных?\n\nЭто действие нельзя отменить.`
    );
    if (!confirmDelete) return;

    try {
      setSending(true);
      await deleteContact(selectedSmtp, activeConversation.contact_email);
      const res = await getConversations(selectedSmtp);
      setConversations(res.data);
      setActiveConversation(null);
      setMessages([]);
      setShowReplyForm(false);
      setAttachments([]);
      setMobileView("list");
      alert(`✅ Контакт ${activeConversation.contact_email} успешно удален из базы`);
    } catch (err) {
      console.error("Ошибка удаления:", err);
      alert("❌ Не удалось удалить контакт: " + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      name: file.name,
      size: file.size,
      file: file,
      data: null
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSendReply = async () => {
    if (!replyText.trim() && attachments.length === 0) {
      alert("Введите текст ответа или прикрепите файл");
      return;
    }

    setSending(true);
    try {
      const attachmentsData = [];
      for (const att of attachments) {
        const base64 = await readFileAsBase64(att.file);
        const base64Data = base64.split(',')[1] || base64;
        attachmentsData.push({
          filename: att.name,
          content: base64Data,
          contentType: att.file.type || 'application/octet-stream'
        });
      }

      const response = await axios.post(
        `${API_URL}/api/reply`,
        {
          conversationId: activeConversation.id,
          text: replyText,
          subject: replySubject,
          attachments: attachmentsData
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        const fileNames = attachments.map(a => a.name).join(', ');
        const newMessage = {
          id: response.data.messageId,
          conversation_id: activeConversation.id,
          sender: "me",
          body: replyText + (attachments.length > 0 ? `\n\n📎 Приложен${attachments.length > 1 ? 'ы' : ''} файл${attachments.length > 1 ? 'ы' : ''}: ${fileNames}` : ''),
          created_at: new Date().toISOString()
        };
        setMessages([...messages, newMessage]);

        const updatedConversations = conversations.map(c =>
          c.id === activeConversation.id
            ? {
                ...c,
                last_message: (replyText.slice(0, 80) || '📎 Файл') + (attachments.length > 0 ? ` (${attachments.length} файл${attachments.length > 1 ? 'а' : ''})` : ''),
                updated_at: new Date().toISOString()
              }
            : c
        );
        setConversations(updatedConversations);
        setReplyText("");
        setAttachments([]);
        setShowReplyForm(false);
      }
    } catch (err) {
      console.error("Ошибка отправки:", err);
      alert("Не удалось отправить ответ: " + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleReply = () => setShowReplyForm(true);
  const handleForward = () => alert("Пересылка пока не реализована");
  const handleAttachFile = () => fileInputRef.current?.click();

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' Б';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' КБ';
    return (bytes / (1024 * 1024)).toFixed(1) + ' МБ';
  };

  // ===== Инициал для аватара =====
  const getInitial = (email) => {
    if (!email) return "?";
    return email[0].toUpperCase();
  };

  return (
    <div className="flex h-[calc(100vh-16px)] md:h-[70vh] bg-gray-50 md:rounded-2xl md:shadow-md overflow-hidden -m-3 md:m-0">

      {/* ===== ЛЕВАЯ ПАНЕЛЬ ===== */}
      <div
        className={`w-full md:w-64 bg-white border-r border-gray-200 flex-col ${
          mobileView === "chat" ? "hidden md:flex" : "flex"
        }`}
      >
        <div className="p-3 border-b border-gray-100">
          <h4 className="text-base md:text-lg font-semibold mb-2">Почта</h4>

          <select
            value={selectedSmtp || ""}
            onChange={(e) => selectSmtp(e.target.value)}
            className="w-full mb-2 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">Выберите почту</option>
            {smtpAccounts.map((s) => (
              <option key={s.id} value={s.id}>{s.email}</option>
            ))}
          </select>

          <button
            onClick={handleSync}
            disabled={syncing || smtpAccounts.length === 0}
            className={`w-full mb-1 px-3 py-2 rounded-xl text-xs md:text-sm font-medium transition ${
              syncing || smtpAccounts.length === 0
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {syncing
              ? "🔄 Обновление..."
              : smtpAccounts.length === 0
                ? "Нет почт"
                : `🔄 Обновить (${smtpAccounts.length})`}
          </button>

          {lastSync && (
            <div className="text-[10px] text-gray-400">Обновлено: {lastSync}</div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-3 pt-2 pb-1">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Диалоги ({conversations.length})
            </h4>
          </div>

          {conversations.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-6 px-4">
              Нет переписок
            </div>
          )}

          {conversations.map((c) => (
            <div
              key={c.id}
              onClick={() => openConversation(c)}
              className={`px-3 py-2.5 mx-2 mb-1 rounded-xl cursor-pointer transition flex items-center gap-3 ${
                activeConversation?.id === c.id
                  ? "bg-blue-50 border border-blue-400"
                  : "hover:bg-gray-50"
              }`}
            >
              {/* Аватар-инициал */}
              <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold ${
                unreadConversations.has(c.id)
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}>
                {getInitial(c.contact_email)}
              </div>

              <div className="flex-1 min-w-0">
<div className="flex items-center justify-between gap-2">
  <strong className="text-sm truncate">{c.contact_email}</strong>
  <div className="flex items-center gap-1 flex-shrink-0">
    {c.is_lead && (
      <span
        className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium"
        title="В лидах"
      >
        🎯
      </span>
    )}
    {unreadConversations.has(c.id) && (
      <span className="w-2 h-2 bg-green-500 rounded-full" />
    )}
  </div>
</div>
                <div className="text-xs text-gray-500 truncate mt-0.5">
                  {c.last_message}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== ПРАВАЯ ПАНЕЛЬ ===== */}
      <div
        className={`flex-1 flex-col bg-white ${
          mobileView === "list" ? "hidden md:flex" : "flex"
        }`}
      >
        {!activeConversation ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 p-6 text-center text-sm">
            Выберите диалог слева
          </div>
        ) : (
          <>
            {/* HEADER ПИСЬМА */}
            <div className="border-b border-gray-200 bg-white">
              <div className="p-3 flex items-start gap-2">
                {/* Кнопка назад — только на мобильном */}
                <button
                  onClick={() => setMobileView("list")}
                  className="md:hidden flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 active:scale-95 transition"
                  title="Назад к списку"
                >
                  ←
                </button>

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">
                    {activeConversation.contact_email}
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">
                    {activeConversation.subject || "(Без темы)"}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {new Date(activeConversation.created_at).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>


              {/* <div className="px-3 pb-3 grid grid-cols-3 md:flex md:gap-2 gap-2">
                <button
                  onClick={handleReply}
                  className="px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition text-xs md:text-sm font-medium"
                >
                  ✏️ Ответить
                </button>
                <button
                  onClick={handleForward}
                  className="px-3 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 transition text-xs md:text-sm font-medium"
                >
                  ↪️ Переслать
                </button>
                <button
                  onClick={handleDeleteFromDatabase}
                  className="px-3 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition text-xs md:text-sm font-medium"
                  disabled={sending}
                >
                  {sending ? "..." : "🗑️ Удалить"}
                </button>
              </div> */}
            <div className="px-3 pb-3 grid grid-cols-2 md:flex md:gap-2 gap-2">
              <button
                onClick={handleReply}
                className="px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition text-xs md:text-sm font-medium"
              >
                ✏️ Ответить
              </button>
              <button
                onClick={handleForward}
                className="px-3 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 transition text-xs md:text-sm font-medium"
              >
                ↪️ Переслать
              </button>
              <button
                onClick={handleMoveToLeads}
                disabled={sending}
                className="px-3 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 transition text-xs md:text-sm font-medium disabled:opacity-50"
              >
                🎯 В лиды
              </button>
              <button
                onClick={handleDeleteFromDatabase}
                disabled={sending}
                className="px-3 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition text-xs md:text-sm font-medium"
              >
                {sending ? "..." : "🗑️ Удалить"}
              </button>
            </div>
            </div>

            {/* BODY ПИСЬМА */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-gray-400 text-center text-sm py-8">Сообщений пока нет</div>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    className={`p-2.5 rounded-2xl max-w-[85%] md:max-w-[75%] ${
                      m.sender === "me"
                        ? "bg-blue-100 ml-auto rounded-br-sm"
                        : "bg-white border border-gray-200 rounded-bl-sm"
                    }`}
                  >
                    {/* Шапка: имя + дата в одной строке */}
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <div className="text-xs font-semibold text-gray-600 truncate">
                        {m.sender === "me" ? "Вы" : activeConversation.contact_email}
                      </div>
                      <div className="text-[10px] text-gray-400 flex-shrink-0">
                        {new Date(m.created_at).toLocaleString('ru-RU', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    </div>

                    {/* Тело сообщения */}
                    <div className="text-sm whitespace-pre-wrap break-words text-gray-800">
                      {m.body}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* ФОРМА ОТВЕТА */}
            {showReplyForm && (
              <div className="border-t border-gray-200 p-3 bg-gray-50">
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Тема</label>
                  <input
                    type="text"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Текст ответа</label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Введите текст ответа..."
                  />
                </div>

                {attachments.length > 0 && (
                  <div className="mb-2">
                    <div className="text-xs font-medium text-gray-600 mb-1">Прикрепленные файлы:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {attachments.map((att, index) => (
                        <div key={index} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs">
                          <span className="truncate max-w-[120px]">📎 {att.name}</span>
                          <span className="text-[10px] text-gray-400">({formatFileSize(att.size)})</span>
                          <button onClick={() => removeAttachment(index)} className="text-red-500 text-sm leading-none">✕</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Кнопка «Прикрепить» — своя строка */}
                <button
                  onClick={handleAttachFile}
                  className="w-full mb-2 px-3 py-2 rounded-xl bg-white border border-gray-300 hover:bg-gray-100 transition text-xs md:text-sm font-medium"
                >
                  📎 Прикрепить файл
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.ppt,.pptx"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Кнопки Отмена / Отправить */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowReplyForm(false);
                      setAttachments([]);
                    }}
                    className="flex-1 px-3 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 transition text-sm font-medium"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleSendReply}
                    disabled={sending}
                    className="flex-1 px-3 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 text-sm font-medium"
                  >
                    {sending ? "Отправка..." : "📤 Отправить"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}